/**
 * Media Viewer Modal
 * Comprehensive viewer for all media types in Design Studio
 * 
 * Supports:
 * - Images (with zoom, pan)
 * - Videos (with controls)
 * - Documents/PDFs (with book view for multi-page)
 * - Archives (info view)
 * 
 * Features:
 * - Fullscreen mode
 * - Keyboard navigation
 * - Touch gestures
 * - Status management (publish/unpublish)
 * - Edit metadata
 * - Share link generation
 * - Download
 */

'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { 
  X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Maximize, Minimize,
  Download, Share2, Play, Pause, Volume2, VolumeX, Loader2,
  BookOpen, Image as ImageIcon, Film, FileText, AlertCircle, Edit2,
  Globe, Lock, Eye, EyeOff, Copy, CheckCircle, RotateCcw, Trash2,
  ExternalLink, Info, Calendar, User, Hash, Tag, Folder
} from 'lucide-react';
import type { MediaFile } from './MediaLibrary';
import notify, { confirm } from '@/lib/notifications';
import { sanitizeUntrustedHtml } from '@/lib/clientHtmlSanitizer';
import { isScreenshotMedia } from './constants';

// Helper function to get auth token
const getAuthToken = (): string | null => {
  try {
    const storedTokens = localStorage.getItem('auth_tokens');
    if (storedTokens) {
      const tokens = JSON.parse(storedTokens);
      return tokens.access_token;
    }
  } catch (error) {
    console.error('[MediaViewer] Failed to load auth token:', error);
  }
  return null;
};

const getFileExtension = (value?: string | null): string => {
  if (!value) return '';

  try {
    const parsed = value.startsWith('http') ? new URL(value) : null;
    const pathname = parsed?.pathname || value;
    const filename = pathname.split('/').pop() || '';
    const cleanFilename = filename.split('?')[0].split('#')[0];
    const lastDotIndex = cleanFilename.lastIndexOf('.');
    return lastDotIndex >= 0 ? cleanFilename.slice(lastDotIndex + 1).toLowerCase() : '';
  } catch {
    const filename = value.split('/').pop() || '';
    const cleanFilename = filename.split('?')[0].split('#')[0];
    const lastDotIndex = cleanFilename.lastIndexOf('.');
    return lastDotIndex >= 0 ? cleanFilename.slice(lastDotIndex + 1).toLowerCase() : '';
  }
};

const isMarkdownDocument = (media: MediaFile | null): boolean => {
  if (!media || media.content_type !== 'document') return false;
  const mimeType = (media.mime_type || '').toLowerCase();
  const fileType = (media.file_type || '').toLowerCase();
  const fileUrlExt = getFileExtension(media.file_url);

  return mimeType === 'text/markdown'
    || fileType === 'md'
    || fileType === 'markdown'
    || fileUrlExt === 'md';
};

const isHtmlDocument = (media: MediaFile | null): boolean => {
  if (!media || media.content_type !== 'document') return false;
  const mimeType = (media.mime_type || '').toLowerCase();
  const fileType = (media.file_type || '').toLowerCase();
  const fileUrlExt = getFileExtension(media.file_url);

  return mimeType === 'text/html'
    || fileType === 'html'
    || fileType === 'htm'
    || fileUrlExt === 'html'
    || fileUrlExt === 'htm';
};

const isTextDocument = (media: MediaFile | null): boolean => {
  if (!media || media.content_type !== 'document') return false;
  const mimeType = (media.mime_type || '').toLowerCase();
  const fileType = (media.file_type || '').toLowerCase();
  const fileUrlExt = getFileExtension(media.file_url);

  return !isHtmlDocument(media) && (
    isMarkdownDocument(media)
      || mimeType.startsWith('text/')
      || ['txt', 'text', 'json', 'csv', 'log'].includes(fileType)
      || ['txt', 'json', 'csv', 'log'].includes(fileUrlExt)
  );
};

const escapeHtml = (value: string) => value
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const renderTextDocumentHtml = (content: string, markdown: boolean) => {
  const escaped = escapeHtml(content || '');

  if (!markdown) {
    return escaped
      .replace(/\n/g, '<br/>');
  }

  return escaped
    .replace(/^### (.*)$/gim, '<h3 class="text-xl font-bold mt-6 mb-3">$1</h3>')
    .replace(/^## (.*)$/gim, '<h2 class="text-2xl font-bold mt-8 mb-4">$1</h2>')
    .replace(/^# (.*)$/gim, '<h1 class="text-3xl font-bold mt-10 mb-6">$1</h1>')
    .replace(/\*\*(.*?)\*\*/gim, '<strong class="font-bold">$1</strong>')
    .replace(/\*(.*?)\*/gim, '<em class="italic">$1</em>')
    .replace(/`([^`]+)`/gim, '<code class="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">$1</code>')
    .replace(/^(\d+)\. (.*)$/gim, '<div class="ml-4 mb-1"><span class="font-semibold">$1.</span> $2</div>')
    .replace(/^[*-] \[( |x|X)\] (.*)$/gim, '<div class="ml-4 mb-1">$1</div>')
    .replace(/^[*-] (.*)$/gim, '<div class="ml-4 mb-1">• $1</div>')
    .replace(/\n\n/gim, '</p><p class="mb-4">')
    .replace(/\n/gim, '<br/>');
};

interface MediaViewerProps {
  isOpen: boolean;
  onClose: () => void;
  media: MediaFile | null;
  onStatusChange?: (mediaId: number, newStatus: 'draft' | 'published' | 'published-resources') => void;
  onPublishToResources?: (media: MediaFile) => void;
  onDelete?: (mediaId: number) => void;
  onEdit?: (media: MediaFile) => void;
  isDark: boolean;
  /** Canonical URL the Share button should copy/show instead of minting a
   *  design-studio share token. Used on surfaces (public Resources) where
   *  media.id is a published_resources id — the createShare API only knows
   *  design_studio_media ids and would 404. No auth required. */
  shareUrl?: string;
  permissions?: {
    can_edit: boolean;
    can_delete: boolean;
    can_publish_resources?: boolean;
    can_share?: boolean;
  };
}

export default function MediaViewer({
  isOpen,
  onClose,
  media,
  onStatusChange,
  onPublishToResources,
  onDelete,
  onEdit,
  shareUrl,
  isDark,
  permissions = {
    can_edit: true,
    can_delete: true,
    can_publish_resources: false
  }
}: MediaViewerProps) {
  // Viewer state
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  
  // Video state
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  
  // Multi-page document state
  const [currentPage, setCurrentPage] = useState(0);
  const [pages, setPages] = useState<any[]>([]);
  const [loadingPages, setLoadingPages] = useState(false);
  const [documentTextContent, setDocumentTextContent] = useState('');
  const [loadingDocumentText, setLoadingDocumentText] = useState(false);
  const [documentTextError, setDocumentTextError] = useState<string | null>(null);
  const sanitizedDocumentSrcDoc = sanitizeUntrustedHtml(
    documentTextContent || '<p style="font-family: sans-serif; padding: 24px; color: #475569;">No HTML brochure content is available.</p>'
  );
  const sanitizedTextDocumentHtml = sanitizeUntrustedHtml(
    `<p class="mb-4">${renderTextDocumentHtml(documentTextContent, isMarkdownDocument(media))}</p>`
  );
  
  // Mobile landscape — video fills the viewport edge-to-edge when the
  // phone rotates (the viewer is already a fixed full-screen surface).
  const [isLandscapeMobile, setIsLandscapeMobile] = useState(false);

  // Share state
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [copied, setCopied] = useState(false);
  
  // Status change state
  const [changingStatus, setChangingStatus] = useState(false);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const mainContentRef = useRef<HTMLDivElement>(null);
  const dragStateRef = useRef<{ startX: number; startY: number; panX: number; panY: number } | null>(null);
  const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isScreenshotAsset = media ? isScreenshotMedia({
    explicitType: media.media_type,
    tags: media.tags,
    metadata: typeof media.metadata === 'string'
      ? (() => {
          try {
            return JSON.parse(media.metadata);
          } catch {
            return null;
          }
        })()
      : media.metadata,
    category: media.category,
    mimeType: media.mime_type,
    contentType: media.content_type,
    fileName: media.file_name,
    title: media.title,
    description: media.description,
  }) : false;

  useEffect(() => {
    const mq = window.matchMedia('(orientation: landscape) and (pointer: coarse)');
    const update = () => setIsLandscapeMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  const exitFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    }
    setIsFullscreen(false);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!isFullscreen) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      exitFullscreen();
    }
  }, [isFullscreen, exitFullscreen]);

  const handleClose = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setIsDragging(false);
    dragStateRef.current = null;
    setIsFullscreen(false);
    setShowInfo(false);
    onClose();
  }, [onClose]);

  const togglePlayPause = useCallback(() => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  }, [isPlaying]);

  const previousPage = useCallback(() => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  }, [currentPage]);

  const nextPage = useCallback(() => {
    if (currentPage < pages.length - 1) {
      setCurrentPage(currentPage + 1);
    }
  }, [currentPage, pages.length]);

  const loadPages = useCallback(async () => {
    if (!media) return;

    setLoadingPages(true);
    try {
      const token = getAuthToken();
      if (!token) throw new Error('No authentication token');

      const response = await fetch(
        `/api/v1/design-studio/media/${media.id}/pages`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setPages(result.data.pages || []);
        }
      }
    } catch (error) {
      console.error('Failed to load pages:', error);
    } finally {
      setLoadingPages(false);
    }
  }, [media]);

  // Auto-hide controls
  useEffect(() => {
    if (showControls && (media?.content_type === 'video' || media?.content_type === 'image')) {
      if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    }
    return () => {
      if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    };
  }, [showControls, media]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isFullscreen) {
          exitFullscreen();
        } else {
          handleClose();
        }
      } else if (e.key === ' ' && media?.content_type === 'video') {
        e.preventDefault();
        togglePlayPause();
      } else if (e.key === 'ArrowLeft' && pages.length > 0) {
        e.preventDefault();
        previousPage();
      } else if (e.key === 'ArrowRight' && pages.length > 0) {
        e.preventDefault();
        nextPage();
      } else if (e.key === 'f' || e.key === 'F') {
        e.preventDefault();
        toggleFullscreen();
      } else if (e.key === 'i' || e.key === 'I') {
        e.preventDefault();
        setShowInfo(!showInfo);
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleKeyPress);
      return () => window.removeEventListener('keydown', handleKeyPress);
    }
  }, [isOpen, media, isFullscreen, isPlaying, showInfo, currentPage, pages, exitFullscreen, handleClose, nextPage, previousPage, toggleFullscreen, togglePlayPause]);

  // Reset state on media change
  useEffect(() => {
    if (media) {
      setZoom(1);
      setPan({ x: 0, y: 0 });
      setIsDragging(false);
      dragStateRef.current = null;
      setCurrentPage(0);
      setShowInfo(false);
      setShowShareDialog(false);
      setIsPlaying(false);
      setVideoProgress(0);

      // Load pages for multi-page documents
      if (media.content_type === 'document' && (media as any).page_count > 1) {
        loadPages();
      }
    }
  }, [media, loadPages]);

  useEffect(() => {
    let cancelled = false;

    const loadDocumentTextPreview = async () => {
      if (!media || (!isHtmlDocument(media) && !isTextDocument(media))) {
        setDocumentTextContent('');
        setDocumentTextError(null);
        setLoadingDocumentText(false);
        return;
      }

      // Page-link resources (e.g. file_url = '/resources?module=…') have no
      // file extension and are not proxyable — skip the preview fetch and
      // show the "open page" state instead of a guaranteed 403.
      if (!getFileExtension(media.file_url)) {
        setDocumentTextContent('');
        setDocumentTextError(null);
        setLoadingDocumentText(false);
        return;
      }

      setLoadingDocumentText(true);
      setDocumentTextError(null);

      try {
        const params = new URLSearchParams({
          url: media.file_url,
          filename: media.file_name || media.title || 'document.txt',
        });

        const response = await fetch(`/api/v1/proxy-file?${params.toString()}`);
        if (!response.ok) {
          throw new Error(`Failed to load document preview (${response.status})`);
        }

        const text = await response.text();
        if (!cancelled) {
          setDocumentTextContent(text);
        }
      } catch (error: any) {
        if (!cancelled) {
          console.error('[MediaViewer] Failed to load text document preview:', error);
          setDocumentTextError(error.message || 'Failed to load preview');
          setDocumentTextContent('');
        }
      } finally {
        if (!cancelled) {
          setLoadingDocumentText(false);
        }
      }
    };

    void loadDocumentTextPreview();

    return () => {
      cancelled = true;
    };
  }, [media]);

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleVideoProgress = () => {
    if (videoRef.current) {
      const progress = (videoRef.current.currentTime / videoRef.current.duration) * 100;
      setVideoProgress(progress);
    }
  };

  const handleVideoLoadedMetadata = () => {
    if (videoRef.current) {
      setVideoDuration(videoRef.current.duration);
    }
  };

  const seekVideo = (e: React.MouseEvent<HTMLDivElement>) => {
    if (videoRef.current) {
      const rect = e.currentTarget.getBoundingClientRect();
      const percent = (e.clientX - rect.left) / rect.width;
      videoRef.current.currentTime = percent * videoRef.current.duration;
    }
  };

  const zoomIn = () => setZoom(Math.min(zoom + 0.5, 5));
  const zoomOut = () => setZoom(Math.max(zoom - 0.5, 0.5));
  const resetZoom = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  // Mouse-wheel zoom for images — zooms toward the cursor position so the
  // point under the pointer stays fixed. Attached non-passively because
  // React registers onWheel as a passive listener (preventDefault would be
  // ignored and the page behind the overlay would scroll).
  useEffect(() => {
    const contentEl = mainContentRef.current;
    if (!isOpen || !contentEl || media?.content_type !== 'image') return;

    const handleWheel = (e: WheelEvent) => {
      const imageEl = imageContainerRef.current;
      if (!imageEl) return;
      e.preventDefault();

      // Cursor offset from the (transformed) image center, in screen px
      const rect = imageEl.getBoundingClientRect();
      const cursorX = e.clientX - (rect.left + rect.width / 2);
      const cursorY = e.clientY - (rect.top + rect.height / 2);

      setZoom((prevZoom) => {
        const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
        const nextZoom = Math.min(5, Math.max(0.5, prevZoom * factor));
        if (nextZoom === prevZoom) return prevZoom;

        // Keep the image point under the cursor stationary:
        // pan' = cursorOffset * (1/z' - 1/z) + (z/z') * pan
        setPan((prevPan) => ({
          x: cursorX * (1 / nextZoom - 1 / prevZoom) + (prevZoom / nextZoom) * prevPan.x,
          y: cursorY * (1 / nextZoom - 1 / prevZoom) + (prevZoom / nextZoom) * prevPan.y,
        }));
        return nextZoom;
      });
    };

    contentEl.addEventListener('wheel', handleWheel, { passive: false });
    return () => contentEl.removeEventListener('wheel', handleWheel);
  }, [isOpen, media]);

  // Click-and-drag panning for images. Pan units are in the scaled transform
  // space, so screen-pixel deltas are divided by the current zoom.
  const handleImageMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    dragStateRef.current = { startX: e.clientX, startY: e.clientY, panX: pan.x, panY: pan.y };
    setIsDragging(true);
  }, [pan]);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const drag = dragStateRef.current;
      if (!drag) return;
      setPan({
        x: drag.panX + (e.clientX - drag.startX) / zoom,
        y: drag.panY + (e.clientY - drag.startY) / zoom,
      });
    };
    const handleMouseUp = () => {
      dragStateRef.current = null;
      setIsDragging(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, zoom]);

  const handleDownload = () => {
    if (!media) return;
    // Route trusted hosts through proxy-file with Content-Disposition:
    // attachment so the browser downloads instead of navigating away
    // (window.open on a raw mp4 replaces the app with the native player).
    const isProxiable = (() => {
      try {
        if (media.file_url.startsWith('/')) return true;
        return [
          'cloud-storage.totalaccess.co.za',
          'dev-cloud-storage.totalaccess.co.za',
          'staticfileserver.totalaccess.co.za',
        ].includes(new URL(media.file_url).host);
      } catch {
        return false;
      }
    })();
    if (!isProxiable) {
      window.open(media.file_url, '_blank');
      return;
    }
    const urlName = media.file_url.split('/').pop()?.split('?')[0];
    const filename = urlName || `${media.file_name || media.title || 'download'}`;
    window.location.href = `/api/v1/proxy-file?url=${encodeURIComponent(media.file_url)}&filename=${encodeURIComponent(filename)}&download=1`;
  };

  const handleGenerateShareLink = async () => {
    if (!media) return;

    if (isScreenshotAsset) {
      notify.info('Screenshots stay internal and cannot be shared publicly.');
      return;
    }
    
    // Check if media is published
    if (media.status !== 'published') {
      notify.warning('Only published media can be shared. Please publish this media first.');
      return;
    }

    // Canonical share URL provided by the host surface (e.g. the public
    // /resources/<id> page) — no token minting needed, works anonymously.
    if (shareUrl) {
      setShareLink(shareUrl);
      setShowShareDialog(true);
      return;
    }

    try {
      const token = getAuthToken();
      if (!token) throw new Error('No authentication token');

      const response = await fetch('/api/v1/design-studio/media', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'createShare',
          mediaId: media.id,
          expiresInDays: 90  // Default 90 days expiration
        })
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        if (result.data.shareUrl) {
          setShareLink(result.data.shareUrl);
          setShowShareDialog(true);
          notify.success('Share link generated successfully!');
        }
      } else {
        notify.error(result.error || 'Failed to generate share link');
      }
    } catch (error: any) {
      console.error('Failed to generate share link:', error);
      notify.error(error.message || 'Failed to generate share link');
    }
  };

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  const handleStatusChange = async (newStatus: 'draft' | 'published' | 'published-resources') => {
    if (!media || !onStatusChange) return;

    if (isScreenshotAsset && newStatus !== 'draft') {
      notify.info('Screenshots stay internal and cannot be published or sent to Resources.');
      return;
    }
    
    // Special handling for publish to resources - open modal instead
    if (newStatus === 'published-resources') {
      if (!permissions.can_publish_resources) {
        notify.error('You do not have permission to publish to resources. Contact an administrator.');
        return;
      }
      
      // Close viewer and open publish to resources modal
      if (onPublishToResources) {
        onPublishToResources(media);
        handleClose();
      } else {
        notify.error('Publish to resources functionality not available');
      }
      return;
    }
    
    // Confirm unpublish
    if ((media.status === 'published' || media.status === 'published-resources') && newStatus === 'draft') {
      confirm({
        title: 'Unpublish Media',
        message: 'Are you sure you want to unpublish this media? It will no longer be accessible to clients.',
        confirmText: 'Unpublish',
        variant: 'warning',
        onConfirm: async () => {
          setChangingStatus(true);
          try {
            await onStatusChange(media.id, newStatus);
          } finally {
            setChangingStatus(false);
          }
        }
      });
      return;
    }
    
    // For other status changes (like draft → published), no confirmation needed
    setChangingStatus(true);
    try {
      await onStatusChange(media.id, newStatus);
    } finally {
      setChangingStatus(false);
    }
  };

  const handleDelete = () => {
    if (!media || !onDelete) return;
    
    confirm({
      title: 'Delete Media',
      message: `Are you sure you want to delete "${media.title}"? This action cannot be undone.`,
      confirmText: 'Delete',
      variant: 'danger',
      onConfirm: () => {
        onDelete(media.id);
        handleClose();
      }
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getStatusBadge = (status: string) => {
    if (isScreenshotAsset) {
      return { label: '🖼️ Screenshot', color: 'bg-sky-500' };
    }

    const badges = {
      draft: { label: '📝 Draft', color: 'bg-gray-500' },
      published: { label: '✓ Published', color: 'bg-green-500' },
      'published-resources': { label: '🌐 Resources', color: 'bg-blue-500' }
    };
    return badges[status as keyof typeof badges] || badges.draft;
  };

  // Early return after all hooks
  if (!isOpen || !media) return null;

  const publishedShareUrl = media.status === 'published'
    ? (shareLink || shareUrl || media.share_url || '')
    : '';

  return (
    <div className="pointer-events-auto fixed inset-0 bg-black z-[300] flex items-center justify-center"
         ref={containerRef}
         onMouseMove={() => setShowControls(true)}>
      
      {/* Top Controls */}
      <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/80 to-transparent p-3 sm:p-6 z-[61]">
        <div className="flex items-start justify-between">
          <div className={`flex-1 transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
            <h2 className="text-sm sm:text-xl md:text-2xl font-bold text-white mb-1 line-clamp-2">{media.title}</h2>
            {media.description && (
              <p className="hidden sm:block text-gray-300 text-sm line-clamp-2 max-w-3xl">{media.description}</p>
            )}
            <div className="flex items-center gap-3 mt-2">
              <span className={`px-2 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs font-medium text-white ${getStatusBadge(media.status).color}`}>
                {getStatusBadge(media.status).label}
              </span>
              {media.tags && media.tags.length > 0 && (
                <div className="flex items-center gap-1">
                  {media.tags.slice(0, 3).map((tag, idx) => (
                    <span key={idx} className="px-2 py-1 rounded bg-white/10 text-white text-xs">
                      {tag}
                    </span>
                  ))}
                  {media.tags.length > 3 && (
                    <span className="text-gray-400 text-xs">+{media.tags.length - 3}</span>
                  )}
                </div>
              )}
            </div>
          </div>
          
          {/* Close button - always visible and clickable, higher z-index than toasts */}
          <button
            onClick={handleClose}
            className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors z-[62] relative"
            title="Close viewer"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div ref={mainContentRef} className={`relative w-full h-full flex items-center justify-center ${isLandscapeMobile && media.content_type === 'video' ? 'p-0' : 'p-4'}`}>
        {/* Image Viewer */}
        {media.content_type === 'image' && (
          <div
            ref={imageContainerRef}
            className="relative select-none"
            style={{
              transform: `scale(${zoom}) translate(${pan.x}px, ${pan.y}px)`,
              transition: isDragging ? 'none' : 'transform 0.15s ease-out',
              cursor: isDragging ? 'grabbing' : 'grab',
            }}
            onMouseDown={handleImageMouseDown}
          >
            <img
              ref={imageRef}
              src={media.file_url}
              alt={media.alt_text || media.title}
              className="max-w-full max-h-[85vh] object-contain pointer-events-none"
              draggable={false}
            />
          </div>
        )}

        {/* Video Viewer */}
        {media.content_type === 'video' && (
          <div className={`relative w-full ${isLandscapeMobile ? 'h-full max-w-none' : 'max-w-6xl'}`}>
            <video
              ref={videoRef}
              src={media.file_url}
              className={isLandscapeMobile ? 'w-full h-full max-h-[100dvh] object-contain' : 'w-full h-auto max-h-[85vh] rounded-lg'}
              onTimeUpdate={handleVideoProgress}
              onLoadedMetadata={handleVideoLoadedMetadata}
              onEnded={() => setIsPlaying(false)}
              poster={media.thumbnail_url}
            />
            
            {/* Video Controls Overlay */}
            <div className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 transition-opacity duration-300 ${
              showControls ? 'opacity-100' : 'opacity-0'
            }`}>
              {/* Progress Bar */}
              <div
                className="w-full h-1 bg-white/20 rounded-full cursor-pointer mb-3"
                onClick={seekVideo}
              >
                <div
                  className="h-full bg-cyan-500 rounded-full"
                  style={{ width: `${videoProgress}%` }}
                />
              </div>
              
              <div className="flex items-center justify-between text-white">
                <div className="flex items-center gap-2">
                  <button
                    onClick={togglePlayPause}
                    className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                  >
                    {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                  </button>
                  <button
                    onClick={toggleMute}
                    className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                  >
                    {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                  </button>
                  <span className="text-sm">
                    {videoRef.current ? formatDuration(videoRef.current.currentTime) : '0:00'} / {formatDuration(videoDuration)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Document/PDF Viewer */}
        {media.content_type === 'document' && (
          <div className="relative w-full max-w-6xl">
            {pages.length > 0 ? (
              <div className="relative">
                <img
                  src={pages[currentPage]?.file_url || media.file_url}
                  alt={`Page ${currentPage + 1}`}
                  className="w-full h-auto max-h-[85vh] object-contain rounded-lg"
                />
                
                {/* Page Navigation */}
                {pages.length > 1 && (
                  <>
                    <button
                      onClick={previousPage}
                      disabled={currentPage === 0}
                      className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-black/50 hover:bg-black/70 text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </button>
                    <button
                      onClick={nextPage}
                      disabled={currentPage === pages.length - 1}
                      className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-black/50 hover:bg-black/70 text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                    >
                      <ChevronRight className="w-6 h-6" />
                    </button>
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-black/70 text-white text-sm">
                      Page {currentPage + 1} of {pages.length}
                    </div>
                  </>
                )}
              </div>
            ) : isHtmlDocument(media) ? (
              <div className="w-full h-[85vh] rounded-lg bg-white overflow-hidden shadow-2xl">
                {loadingDocumentText ? (
                  <div className="w-full h-full flex items-center justify-center text-gray-600 gap-3">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Loading brochure preview...</span>
                  </div>
                ) : documentTextError ? (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-3 px-6 text-center text-gray-600">
                    <AlertCircle className="w-8 h-8 text-amber-500" />
                    <p>{documentTextError}</p>
                    <button
                      onClick={handleDownload}
                      className="px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-700 transition-colors"
                    >
                      Open original file
                    </button>
                  </div>
                ) : (
                  <iframe
                    title={media.title}
                    srcDoc={sanitizedDocumentSrcDoc}
                    sandbox="allow-same-origin"
                    className="w-full h-full bg-white"
                  />
                )}
              </div>
            ) : isTextDocument(media) ? (
              <div className="w-full h-[85vh] rounded-lg bg-white overflow-hidden shadow-2xl">
                {!getFileExtension(media.file_url) ? (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-3 px-6 text-center text-gray-600">
                    <AlertCircle className="w-8 h-8 text-blue-500" />
                    <p>This resource links to a page — open it directly.</p>
                    <a
                      href={media.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-700 transition-colors"
                    >
                      Open page
                    </a>
                  </div>
                ) : loadingDocumentText ? (
                  <div className="w-full h-full flex items-center justify-center text-gray-600 gap-3">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Loading document preview...</span>
                  </div>
                ) : documentTextError ? (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-3 px-6 text-center text-gray-600">
                    <AlertCircle className="w-8 h-8 text-amber-500" />
                    <p>{documentTextError}</p>
                    <button
                      onClick={handleDownload}
                      className="px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-700 transition-colors"
                    >
                      Open original file
                    </button>
                  </div>
                ) : (
                  <div className="w-full h-full overflow-auto p-8">
                    <div
                      className="prose prose-lg max-w-none text-gray-900"
                      dangerouslySetInnerHTML={{
                        __html: sanitizedTextDocumentHtml,
                      }}
                    />
                  </div>
                )}
              </div>
            ) : (
              <iframe
                src={media.mime_type === 'application/pdf'
                  ? media.file_url
                  : `/api/v1/proxy-file?${new URLSearchParams({
                      url: media.file_url,
                      filename: media.file_name || media.title || 'document',
                    }).toString()}`}
                className="w-full h-[85vh] rounded-lg bg-white"
                title={media.title}
              />
            )}
          </div>
        )}

        {/* Archive/Other Viewer */}
        {(media.content_type === 'archive' || media.content_type === 'other') && (
          <div className="text-center text-white">
            <FileText className="w-24 h-24 mx-auto mb-4 opacity-50" />
            <h3 className="text-xl font-semibold mb-2">{media.file_type.toUpperCase()} File</h3>
            <p className="text-gray-400 mb-4">Preview not available for this file type</p>
            <button
              onClick={handleDownload}
              className="px-6 py-3 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg flex items-center gap-2 mx-auto transition-colors"
            >
              <Download className="w-5 h-5" />
              Download File ({formatFileSize(media.file_size)})
            </button>
          </div>
        )}
      </div>

      {/* Bottom Controls */}
      <div className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 transition-opacity duration-300 ${
        showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}>
        <div className="flex items-center justify-between">
          {/* Left Controls */}
          <div className="flex items-center gap-2">
            {/* Zoom Controls (for images) */}
            {media.content_type === 'image' && (
              <>
                <button
                  onClick={zoomOut}
                  disabled={zoom <= 0.5}
                  className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ZoomOut className="w-5 h-5" />
                </button>
                <span className="text-white text-sm px-2">{Math.round(zoom * 100)}%</span>
                <button
                  onClick={zoomIn}
                  disabled={zoom >= 5}
                  className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ZoomIn className="w-5 h-5" />
                </button>
                <button
                  onClick={resetZoom}
                  className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
                >
                  <RotateCcw className="w-5 h-5" />
                </button>
              </>
            )}
            
            <button
              onClick={() => setShowInfo(!showInfo)}
              className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
            >
              <Info className="w-5 h-5" />
            </button>
          </div>

          {/* Right Controls */}
          <div className="flex items-center gap-2">
            {/* Status Change Buttons */}
            {permissions.can_edit && (
              <>
                {!isScreenshotAsset && media.status === 'draft' && (
                  <button
                    onClick={() => handleStatusChange('published')}
                    disabled={changingStatus}
                    className="px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white flex items-center gap-2 transition-colors disabled:opacity-50"
                  >
                    <Eye className="w-4 h-4" />
                    Publish
                  </button>
                )}

                {!isScreenshotAsset && media.status === 'published' && (
                  <>
                    <button
                      onClick={() => handleStatusChange('draft')}
                      disabled={changingStatus}
                      className="px-4 py-2 rounded-lg bg-gray-600 hover:bg-gray-700 text-white flex items-center gap-2 transition-colors disabled:opacity-50"
                    >
                      <EyeOff className="w-4 h-4" />
                      Unpublish
                    </button>
                    {permissions.can_publish_resources && (
                      <button
                        onClick={() => handleStatusChange('published-resources')}
                        disabled={changingStatus}
                        className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2 transition-colors disabled:opacity-50"
                      >
                        <Globe className="w-4 h-4" />
                        Publish to Resources
                      </button>
                    )}
                  </>
                )}

                {!isScreenshotAsset && media.status === 'published-resources' && (
                  <button
                    onClick={() => handleStatusChange('published')}
                    disabled={changingStatus}
                    className="px-4 py-2 rounded-lg bg-gray-600 hover:bg-gray-700 text-white flex items-center gap-2 transition-colors disabled:opacity-50"
                  >
                    <Lock className="w-4 h-4" />
                    Make Private
                  </button>
                )}

                {isScreenshotAsset && media.status !== 'draft' && (
                  <button
                    onClick={() => handleStatusChange('draft')}
                    disabled={changingStatus}
                    className="px-4 py-2 rounded-lg bg-gray-600 hover:bg-gray-700 text-white flex items-center gap-2 transition-colors disabled:opacity-50"
                  >
                    <Lock className="w-4 h-4" />
                    Make Internal
                  </button>
                )}
              </>
            )}

            {/* Share button - only for published media, when sharing is allowed */}
            {!isScreenshotAsset && (permissions.can_share ?? true) && media.status === 'published' && (
              <button
                onClick={handleGenerateShareLink}
                className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
                title="Generate Share Link"
              >
                <Share2 className="w-5 h-5" />
              </button>
            )}

            <button
              onClick={handleDownload}
              className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
              title="Download"
            >
              <Download className="w-5 h-5" />
            </button>

            {permissions.can_edit && onEdit && (
              <button
                onClick={() => {
                  handleClose();
                  onEdit(media);
                }}
                className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
                title="Edit"
              >
                <Edit2 className="w-5 h-5" />
              </button>
            )}

            {permissions.can_delete && (
              <button
                onClick={handleDelete}
                className="p-2 rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors"
                title="Delete"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            )}

            <button
              onClick={toggleFullscreen}
              className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
              title="Fullscreen"
            >
              {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Info Panel */}
      {showInfo && (
        <div className="absolute right-0 top-0 bottom-0 w-96 bg-black/90 backdrop-blur-sm p-6 overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-white">Media Info</h3>
            <button
              onClick={() => setShowInfo(false)}
              className="p-1 rounded hover:bg-white/10 text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4 text-sm">
            <div>
              <div className="text-gray-400 mb-1">File Name</div>
              <div className="text-white">{media.file_name}</div>
            </div>

            <div>
              <div className="text-gray-400 mb-1">Type</div>
              <div className="text-white capitalize">{media.content_type}</div>
            </div>

            {isScreenshotAsset && (
              <div>
                <div className="text-gray-400 mb-1">Visibility</div>
                <div className="text-white">Internal screenshot only</div>
              </div>
            )}

            <div>
              <div className="text-gray-400 mb-1">Size</div>
              <div className="text-white">{formatFileSize(media.file_size)}</div>
            </div>

            {media.width && media.height && (
              <div>
                <div className="text-gray-400 mb-1">Dimensions</div>
                <div className="text-white">{media.width} × {media.height}</div>
              </div>
            )}

            {media.duration && (
              <div>
                <div className="text-gray-400 mb-1">Duration</div>
                <div className="text-white">{formatDuration(media.duration)}</div>
              </div>
            )}

            <div>
              <div className="text-gray-400 mb-1">Purpose</div>
              <div className="text-white capitalize">{media.media_purpose.replace('-', ' ')}</div>
            </div>

            {media.category && (
              <div>
                <div className="text-gray-400 mb-1">Category</div>
                <div className="text-white">{media.category}</div>
              </div>
            )}

            {media.tags && media.tags.length > 0 && (
              <div>
                <div className="text-gray-400 mb-1">Tags</div>
                <div className="flex flex-wrap gap-1">
                  {media.tags.map((tag, idx) => (
                    <span key={idx} className="px-2 py-1 rounded bg-white/10 text-white text-xs">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Share Link - Only shown for published media */}
            {!isScreenshotAsset && media.status === 'published' && (
              <div className="col-span-2 border-t border-white/10 pt-3">
                <div className="text-gray-400 mb-2 flex items-center gap-2">
                  <Share2 className="w-4 h-4" />
                  Share Link
                </div>
                {publishedShareUrl ? (
                  <>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={publishedShareUrl}
                        readOnly
                        className="flex-1 px-3 py-2 rounded bg-white/5 border border-white/10 text-white text-sm"
                        onClick={(e) => {
                          const input = e.target as HTMLInputElement;
                          input.select();
                        }}
                      />
                      <button
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(publishedShareUrl);
                            notify.success('Link copied to clipboard!');
                          } catch (err) {
                            console.error('Failed to copy:', err);
                            notify.error('Failed to copy link');
                          }
                        }}
                        className="px-3 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded flex items-center gap-2 transition-colors"
                      >
                        <Copy className="w-4 h-4" />
                        Copy
                      </button>
                      <button
                        onClick={() => {
                          window.open(publishedShareUrl, '_blank');
                        }}
                        className="px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded flex items-center gap-2 transition-colors"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      This link uses the canonical public share viewer for this media.
                    </div>
                  </>
                ) : (
                  <div className="space-y-2">
                    <div className="text-xs text-gray-500">
                      Generate or refresh the public share link to use the current cloud-storage viewer.
                    </div>
                    <button
                      onClick={handleGenerateShareLink}
                      className="px-3 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded flex items-center gap-2 transition-colors"
                    >
                      <Share2 className="w-4 h-4" />
                      Generate share link
                    </button>
                  </div>
                )}
              </div>
            )}

            <div>
              <div className="text-gray-400 mb-1">Created</div>
              <div className="text-white">{new Date(media.created_at).toLocaleString()}</div>
            </div>

            <div>
              <div className="text-gray-400 mb-1">Views</div>
              <div className="text-white">{media.view_count}</div>
            </div>

            <div>
              <div className="text-gray-400 mb-1">Downloads</div>
              <div className="text-white">{media.download_count}</div>
            </div>
          </div>
        </div>
      )}

      {/* Share Dialog */}
      {showShareDialog && shareLink && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className={`w-full max-w-md rounded-lg p-6 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Share Link
              </h3>
              <button
                onClick={() => setShowShareDialog(false)}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Anyone with this link can view this media
            </p>
            
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                value={shareLink}
                readOnly
                className={`flex-1 px-3 py-2 rounded-lg border ${
                  isDark 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-gray-50 border-gray-300 text-gray-900'
                }`}
              />
              <button
                onClick={copyShareLink}
                className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg flex items-center gap-2 transition-colors"
              >
                {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            
            <button
              onClick={() => window.open(shareLink, '_blank')}
              className={`w-full px-4 py-2 rounded-lg flex items-center justify-center gap-2 transition-colors ${
                isDark 
                  ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
              }`}
            >
              <ExternalLink className="w-4 h-4" />
              Open in New Tab
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
